import React, { useState, useEffect, useCallback } from 'react';
import { AlertContact } from '../../types';
import { sectionStyle, btnStyle, inputStyle, labelStyle } from './surveillanceShared';
import { testContact } from '../../services/api';
import { useContactForm } from './hooks/useContactForm';
import ErrorBanner from '../shared/ErrorBanner';
import { getAllTimezones } from '../../utils/timezone';
import { urlBase64ToUint8Array } from '../../utils/push';
import { fetchVapidPublicKey, registerPushSubscription, unregisterPushSubscription } from '../../services/alertsApi';

const TZ_OPTIONS = getAllTimezones();

// E.164-style: digits only, country code first (1-9), 7-15 digits total. No '+' or leading '0'.
const PHONE_RE = /^[1-9]\d{6,14}$/;
const PHONE_HELP = 'Bara siffror, med landsnummer. Inget + och ingen inledande nolla. Exempel: 4612345678';

// Days bitmask: Sun=1, Mon=2, Tue=4, Wed=8, Thu=16, Fri=32, Sat=64
const DAY_LABELS: { bit: number; label: string }[] = [
    { bit:  2, label: 'Mån' },
    { bit:  4, label: 'Tis' },
    { bit:  8, label: 'Ons' },
    { bit: 16, label: 'Tors' },
    { bit: 32, label: 'Fre' },
    { bit: 64, label: 'Lör' },
    { bit:  1, label: 'Sön' },
];
const ALL_DAYS = 127;
const WEEKDAYS = 2 + 4 + 8 + 16 + 32;

const formatHoursSummary = (start: string | null, end: string | null, mask: number): string => {
    const days = mask === ALL_DAYS
        ? 'varje dag'
        : mask === WEEKDAYS
            ? 'Mån–fre'
            : DAY_LABELS.filter(d => mask & d.bit).map(d => d.label).join(',') || '—';
    if (!start || !end) return `Dygnet runt (${days})`;
    return `${start.slice(0, 5)}–${end.slice(0, 5)} (${days})`;
};

type TestState = Record<string, 'loading' | 'ok' | 'error'>;

/**
 * Kontaktlista med formulär, testknappar, push-prenumeration och regeltilldelning.
 * Äger sitt eget state via useContactForm().
 */
const ContactsTab: React.FC = () => {
    const {
        contacts, rules, advancedRules, error, setError,
        showForm: showContactForm, editContactId, form: contactForm, setForm: setContactForm,
        openCreate: onOpenCreate, openEdit: onOpenEdit, cancel: onCancelForm, submit: onSubmitForm,
        remove: onDeleteContact, setRulesFor: onSetContactRules,
    } = useContactForm();
    const onTestContact = testContact;
    const [testState, setTestState] = useState<TestState>({});
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [assignContactId, setAssignContactId] = useState<number | null>(null);
    const [assignRuleIds, setAssignRuleIds] = useState<Set<number>>(new Set());
    const [assignAdvancedIds, setAssignAdvancedIds] = useState<Set<number>>(new Set());
    const [assignLoading, setAssignLoading] = useState(false);

    // Push subscription state
    const [vapidKey, setVapidKey] = useState<string>('');
    const [currentEndpoint, setCurrentEndpoint] = useState<string | null>(null);
    // contactId -> 'subscribed' | 'loading' | null
    const [pushSubState, setPushSubState] = useState<Record<number, 'subscribed' | 'loading'>>({});

    // On mount: fetch VAPID key and check if this browser already has a push subscription
    useEffect(() => {
        fetchVapidPublicKey().then(key => setVapidKey(key));
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then(reg => {
                reg.pushManager.getSubscription().then(sub => {
                    if (sub) setCurrentEndpoint(sub.endpoint);
                });
            });
        }
    }, []);

    const handleSubscribePush = useCallback(async (contact: AlertContact) => {
        if (!vapidKey) { alert('Push-notiser är inte inställda på servern (VAPID-nyckeln saknas).'); return; }
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            alert('Den här webbläsaren stöder inte push-notiser.'); return;
        }
        setPushSubState(s => ({ ...s, [contact.id]: 'loading' }));
        try {
            const perm = await Notification.requestPermission();
            if (perm !== 'granted') { alert('Du nekade tillstånd för notiser.'); setPushSubState(s => { const n = {...s}; delete n[contact.id]; return n; }); return; }
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidKey),
            });
            await registerPushSubscription(contact.id, sub);
            setCurrentEndpoint(sub.endpoint);
            setPushSubState(s => ({ ...s, [contact.id]: 'subscribed' }));
        } catch (err: any) {
            alert(`Kunde inte starta prenumerationen: ${err.message}`);
            setPushSubState(s => { const n = {...s}; delete n[contact.id]; return n; });
        }
    }, [vapidKey]);

    const handleUnsubscribePush = useCallback(async (contact: AlertContact) => {
        if (!currentEndpoint) return;
        setPushSubState(s => ({ ...s, [contact.id]: 'loading' }));
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) await sub.unsubscribe();
            await unregisterPushSubscription(contact.id, currentEndpoint);
            setCurrentEndpoint(null);
            setPushSubState(s => { const n = {...s}; delete n[contact.id]; return n; });
        } catch (err: any) {
            alert(`Failed to unsubscribe: ${err.message}`);
            setPushSubState(s => { const n = {...s}; delete n[contact.id]; return n; });
        }
    }, [currentEndpoint]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!contactForm.email.trim()) {
            setEmailError('E-postadress måste fyllas i. Egenkontrollens utskick går bara med e-post.');
            return;
        }
        setEmailError(null);
        const phone = contactForm.phone.trim();
        if (contactForm.notify_sms && !phone) {
            setPhoneError('Telefonnummer måste fyllas i när SMS är påslaget.');
            return;
        }
        if (phone && !PHONE_RE.test(phone)) {
            setPhoneError(PHONE_HELP);
            return;
        }
        setPhoneError(null);
        onSubmitForm(e);
    };

    const openAssignRules = (contact: AlertContact) => {
        setAssignRuleIds(new Set(
            rules.filter(r => r.contacts.some(c => c.id === contact.id)).map(r => r.id)
        ));
        setAssignAdvancedIds(new Set(
            advancedRules.filter(r => (r.contacts ?? []).some(c => c.id === contact.id)).map(r => r.id)
        ));
        setAssignContactId(contact.id);
    };

    const toggleIn = (setter: React.Dispatch<React.SetStateAction<Set<number>>>, id: number) => {
        setter(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const submitAssign = async () => {
        if (assignContactId === null) return;
        setAssignLoading(true);
        try {
            await onSetContactRules(
                assignContactId,
                Array.from(assignRuleIds),
                Array.from(assignAdvancedIds),
            );
            setAssignContactId(null);
        } finally {
            setAssignLoading(false);
        }
    };

    const handleTest = async (contactId: number, channel: 'email' | 'sms' | 'push') => {
        const key = `${contactId}-${channel}`;
        setTestState(s => ({ ...s, [key]: 'loading' }));
        try {
            await onTestContact(contactId, channel);
            setTestState(s => ({ ...s, [key]: 'ok' }));
        } catch {
            setTestState(s => ({ ...s, [key]: 'error' }));
        } finally {
            setTimeout(() => setTestState(s => { const n = { ...s }; delete n[key]; return n; }), 3000);
        }
    };

    const testBtn = (contact: AlertContact, channel: 'email' | 'sms' | 'push') => {
        if (channel === 'email' && (!contact.notify_email || !contact.email)) return null;
        if (channel === 'sms' && (!contact.notify_sms || !contact.phone)) return null;
        if (channel === 'push' && !contact.notify_push) return null;
        const key = `${contact.id}-${channel}`;
        const state = testState[key];
        const label = state === 'loading' ? '…' : state === 'ok' ? '✓' : state === 'error' ? '✗'
            : channel === 'email' ? '✉ Testa' : channel === 'sms' ? '📱 Testa' : '🔔 Testa';
        const color = state === 'ok' ? '#28a745' : state === 'error' ? '#dc3545' : '#6c757d';
        return (
            <button
                key={key}
                onClick={() => handleTest(contact.id, channel)}
                disabled={state === 'loading'}
                style={btnStyle(color)}
            >
                {label}
            </button>
        );
    };

    return (
    <div>
        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        {/* Assign to Rules modal */}
        {assignContactId !== null && (() => {
            const contact = contacts.find(c => c.id === assignContactId);
            return (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                }}>
                    <div style={{
                        backgroundColor: 'white', borderRadius: '8px', padding: '24px',
                        minWidth: '340px', maxWidth: '520px', width: '90%',
                        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                    }}>
                        <h3 style={{ margin: '0 0 4px 0', fontSize: '16px' }}>Vilka larm ska {contact?.name} få?</h3>
                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
                            Kryssa i de larm som ska gå till <strong>{contact?.name}</strong>. Avkryssade tas bort.
                        </div>
                        {rules.length === 0 && advancedRules.length === 0 ? (
                            <div style={{ color: '#888', fontSize: '13px', marginBottom: '16px' }}>Inga larmregler finns ännu.</div>
                        ) : (
                            <div style={{ overflowY: 'auto', flex: 1, marginBottom: '16px', border: '1px solid #ddd', borderRadius: '4px' }}>
                                {rules.map(rule => {
                                    const checked = assignRuleIds.has(rule.id);
                                    return (
                                        <label key={`r${rule.id}`} style={{
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                            padding: '10px 12px', borderBottom: '1px solid #f0f0f0',
                                            cursor: 'pointer', backgroundColor: checked ? '#f0f7ff' : 'white',
                                        }}>
                                            <input type="checkbox" checked={checked}
                                                onChange={() => toggleIn(setAssignRuleIds, rule.id)} />
                                            <div>
                                                <div style={{ fontWeight: 500, fontSize: '13px' }}>{rule.name}</div>
                                                {rule.group_name && (
                                                    <div style={{ fontSize: '11px', color: '#888' }}>{rule.group_name}</div>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })}
                                {advancedRules.length > 0 && (
                                    <div style={{
                                        padding: '6px 12px', fontSize: '11px', fontWeight: 700, color: '#6a1b9a',
                                        backgroundColor: '#f7f2fa', borderBottom: '1px solid #f0f0f0',
                                    }}>
                                        AVANCERADE
                                    </div>
                                )}
                                {advancedRules.map(rule => {
                                    const checked = assignAdvancedIds.has(rule.id);
                                    return (
                                        <label key={`a${rule.id}`} style={{
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                            padding: '10px 12px', borderBottom: '1px solid #f0f0f0',
                                            cursor: 'pointer', backgroundColor: checked ? '#f7f2fa' : 'white',
                                        }}>
                                            <input type="checkbox" checked={checked}
                                                onChange={() => toggleIn(setAssignAdvancedIds, rule.id)} />
                                            <div>
                                                <div style={{ fontWeight: 500, fontSize: '13px' }}>{rule.name ?? `Regel #${rule.id}`}</div>
                                                <div style={{ fontSize: '11px', color: '#888' }}>
                                                    {rule.device_name ?? rule.ieee_address} · tröskel
                                                </div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setAssignContactId(null)} style={btnStyle('#6c757d')} disabled={assignLoading}>
                                Avbryt
                            </button>
                            <button
                                onClick={submitAssign} style={btnStyle('#007bff')}
                                disabled={assignLoading || (rules.length === 0 && advancedRules.length === 0)}
                            >
                                {assignLoading ? 'Sparar…' : 'Spara'}
                            </button>
                        </div>
                    </div>
                </div>
            );
        })()}

        <div style={{ marginBottom: '16px' }}>
            <button onClick={onOpenCreate} style={btnStyle('#28a745')}>+ Ny kontakt</button>
        </div>

        {showContactForm && (
            <div style={{ ...sectionStyle, borderColor: '#007bff' }}>
                <h3 style={{ marginTop: 0 }}>{editContactId ? 'Ändra kontakt' : 'Ny kontakt'}</h3>
                <div style={{
                    padding: '10px 12px', marginBottom: '16px', borderRadius: '6px',
                    backgroundColor: '#e7f1ff', border: '1px solid #b6d4fe', color: '#0c3d78', fontSize: '13px',
                }}>
                    <b>Så används kanalerna:</b> sms skickas bara vid larm under Bevakning.
                    Egenkontrollens påminnelser, avvikelser och "klart"-besked skickas alltid med e-post,
                    därför måste varje kontakt ha en e-postadress.
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div>
                            <label style={labelStyle}>Namn</label>
                            <input style={inputStyle} value={contactForm.name} required
                                onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))} />
                        </div>
                        <div>
                            <label style={labelStyle}>E-post *</label>
                            <input
                                style={{ ...inputStyle, borderColor: emailError ? '#dc3545' : undefined }}
                                type="email" required value={contactForm.email} placeholder="namn@exempel.se"
                                onChange={e => {
                                    setContactForm(p => ({ ...p, email: e.target.value }));
                                    if (emailError) setEmailError(null);
                                }} />
                            <div style={{ fontSize: '11px', color: emailError ? '#dc3545' : '#888', marginTop: '4px' }}>
                                {emailError ?? 'Krävs. Egenkontrollen skickar alltid hit.'}
                            </div>
                        </div>
                        <div>
                            <label style={labelStyle}>Telefon (sms, bara larm)</label>
                            <input
                                style={{
                                    ...inputStyle,
                                    borderColor: phoneError ? '#dc3545' : (inputStyle.border ? undefined : '#ddd'),
                                }}
                                value={contactForm.phone}
                                placeholder="4612345678"
                                inputMode="numeric"
                                pattern="[1-9][0-9]{6,14}"
                                maxLength={15}
                                title={PHONE_HELP}
                                onChange={e => {
                                    // Strip non-digits as the user types
                                    const cleaned = e.target.value.replace(/\D/g, '');
                                    setContactForm(p => ({ ...p, phone: cleaned }));
                                    if (phoneError) setPhoneError(null);
                                }}
                            />
                            <div style={{ fontSize: '11px', color: phoneError ? '#dc3545' : '#888', marginTop: '4px' }}>
                                {phoneError ?? PHONE_HELP}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={contactForm.notify_email}
                                onChange={e => setContactForm(p => ({ ...p, notify_email: e.target.checked }))} />
                            📧 Skicka e-post vid larm
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={contactForm.notify_sms}
                                onChange={e => setContactForm(p => ({ ...p, notify_sms: e.target.checked }))} />
                            📱 Skicka sms vid larm
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={contactForm.notify_push}
                                onChange={e => setContactForm(p => ({ ...p, notify_push: e.target.checked }))} />
                            🔔 Skicka push-notis
                        </label>
                    </div>

                    {/* Alert active hours */}
                    <div style={{ borderTop: '1px solid #eee', paddingTop: '12px', marginBottom: '16px' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#444', marginBottom: '8px' }}>
                            🔔 När larm får skickas
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                            Larm skickas till den här kontakten bara under den valda tiden.
                            Lämna tiderna tomma för att tillåta larm dygnet runt de valda dagarna.
                            Tiderna gäller i kontaktens tidszon.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                            <div>
                                <label style={labelStyle}>Från (tt:mm)</label>
                                <input style={inputStyle} type="time" value={contactForm.alert_hours_start}
                                    onChange={e => setContactForm(p => ({ ...p, alert_hours_start: e.target.value }))} />
                            </div>
                            <div>
                                <label style={labelStyle}>Till (tt:mm)</label>
                                <input style={inputStyle} type="time" value={contactForm.alert_hours_end}
                                    onChange={e => setContactForm(p => ({ ...p, alert_hours_end: e.target.value }))} />
                            </div>
                            <div>
                                <label style={labelStyle}>Tidszon</label>
                                <select style={inputStyle} value={contactForm.timezone}
                                    onChange={e => setContactForm(p => ({ ...p, timezone: e.target.value }))}>
                                    {TZ_OPTIONS.map(tz => (
                                        <option key={tz} value={tz}>{tz}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
                            {DAY_LABELS.map(d => {
                                const on = (contactForm.alert_days_mask & d.bit) !== 0;
                                return (
                                    <button key={d.bit} type="button"
                                        onClick={() => setContactForm(p => ({
                                            ...p,
                                            alert_days_mask: on ? (p.alert_days_mask & ~d.bit) : (p.alert_days_mask | d.bit),
                                        }))}
                                        style={{
                                            padding: '6px 12px', borderRadius: '4px',
                                            border: '1px solid ' + (on ? '#007bff' : '#ddd'),
                                            backgroundColor: on ? '#007bff' : 'white',
                                            color: on ? 'white' : '#555',
                                            cursor: 'pointer', fontSize: '12px', fontWeight: 'bold',
                                        }}>
                                        {d.label}
                                    </button>
                                );
                            })}
                            <button type="button"
                                onClick={() => setContactForm(p => ({ ...p, alert_days_mask: ALL_DAYS }))}
                                style={{ padding: '6px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                                Varje dag
                            </button>
                            <button type="button"
                                onClick={() => setContactForm(p => ({ ...p, alert_days_mask: WEEKDAYS }))}
                                style={{ padding: '6px 12px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                                Mån–fre
                            </button>
                        </div>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', marginTop: '8px' }}>
                            <input type="checkbox" style={{ marginTop: '3px' }} checked={contactForm.defer_offhours}
                                onChange={e => setContactForm(p => ({ ...p, defer_offhours: e.target.checked }))} />
                            <span style={{ fontSize: '12px', color: '#444' }}>
                                <strong>Skicka sammanställning efter arbetstid.</strong> Larm utanför fönstret
                                slängs inte — du får ett samlat meddelande med länk till alla larm nästa gång
                                du är i tjänst, grupperat per larmregel.
                            </span>
                        </label>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="submit" style={btnStyle('#007bff')}>{editContactId ? 'Spara' : 'Skapa kontakt'}</button>
                        <button type="button" onClick={onCancelForm} style={btnStyle('#6c757d')}>Avbryt</button>
                    </div>
                </form>
            </div>
        )}

        {contacts.length === 0 ? (
            <div style={{ ...sectionStyle, textAlign: 'center', color: '#666' }}>
                Inga kontakter än. Lägg till en kontakt för att kunna ta emot larm.
            </div>
        ) : (
            <>
                {/* Desktop table view */}
                <table className="contacts-table-desktop" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd', borderRadius: '4px' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #ddd' }}>
                            <th style={{ padding: '12px', textAlign: 'left' }}>Namn</th>
                            <th style={{ padding: '12px', textAlign: 'left' }}>E-post</th>
                            <th style={{ padding: '12px', textAlign: 'left' }}>Telefon</th>
                            <th style={{ padding: '12px', textAlign: 'left' }}>Sätt att nå</th>
                            <th style={{ padding: '12px', textAlign: 'left' }}>Larmtider</th>
                            <th style={{ padding: '12px', textAlign: 'left' }}>Åtgärder</th>
                        </tr>
                    </thead>
                    <tbody>
                        {contacts.map(c => (
                            <tr key={c.id} style={{ borderBottom: '1px solid #ddd' }}>
                                <td style={{ padding: '12px' }}><strong>{c.name}</strong></td>
                                <td style={{ padding: '12px', color: '#555' }}>{c.email || <span style={{ color: '#aaa' }}>—</span>}</td>
                                <td style={{ padding: '12px', color: '#555' }}>{c.phone || <span style={{ color: '#aaa' }}>—</span>}</td>
                                <td style={{ padding: '12px' }}>
                                    {c.notify_email && <span style={{ marginRight: '6px' }}>📧</span>}
                                    {c.notify_sms && <span style={{ marginRight: '6px' }}>📱</span>}
                                    {c.notify_push && <span style={{ marginRight: '6px' }}>🔔</span>}
                                </td>
                                <td style={{ padding: '12px', color: '#555', fontSize: '12px' }}>
                                    {formatHoursSummary(c.alert_hours_start, c.alert_hours_end, c.alert_days_mask ?? ALL_DAYS)}
                                </td>
                                <td style={{ padding: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                    {testBtn(c, 'email')}
                                    {testBtn(c, 'sms')}
                                    {testBtn(c, 'push')}
                                    {c.notify_push && (() => {
                                        const isThisDeviceSubscribed = currentEndpoint !== null;
                                        const loading = pushSubState[c.id] === 'loading';
                                        if (isThisDeviceSubscribed) {
                                            return (
                                                <button
                                                    onClick={() => handleUnsubscribePush(c)}
                                                    disabled={loading}
                                                    style={btnStyle('#6c757d')}
                                                >
                                                    {loading ? '…' : '🔕 Avprenumerera'}
                                                </button>
                                            );
                                        }
                                        return (
                                            <button
                                                onClick={() => handleSubscribePush(c)}
                                                disabled={loading}
                                                style={btnStyle('#17a2b8')}
                                            >
                                                {loading ? '…' : '🔔 Prenumerera'}
                                            </button>
                                        );
                                    })()}
                                    <button onClick={() => openAssignRules(c)} style={btnStyle('#17a2b8')}>Koppla till regler</button>
                                    <button onClick={() => onOpenEdit(c)} style={btnStyle('#ffc107', '#000')}>Ändra</button>
                                    <button onClick={() => onDeleteContact(c.id)} style={btnStyle('#dc3545')}>Ta bort</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Mobile card view */}
                <div className="contacts-cards-mobile">
                    {contacts.map(c => (
                        <div key={c.id} style={{
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            padding: '14px',
                            backgroundColor: 'white',
                            marginBottom: '12px',
                        }}>
                            <div style={{ marginBottom: '12px' }}>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '6px' }}>{c.name}</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#555' }}>
                                    {c.email && <div>📧 {c.email}</div>}
                                    {c.phone && <div>📱 {c.phone}</div>}
                                </div>
                            </div>
                            
                            <div style={{ marginBottom: '12px', fontSize: '13px' }}>
                                <div style={{ color: '#666', fontWeight: 500, marginBottom: '4px' }}>Kanaler:</div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    {c.notify_email && <span style={{ backgroundColor: '#e3f2fd', padding: '4px 8px', borderRadius: '4px' }}>📧 E-post</span>}
                                    {c.notify_sms && <span style={{ backgroundColor: '#e3f2fd', padding: '4px 8px', borderRadius: '4px' }}>📱 SMS</span>}
                                    {c.notify_push && <span style={{ backgroundColor: '#e3f2fd', padding: '4px 8px', borderRadius: '4px' }}>🔔 Push</span>}
                                </div>
                            </div>
                            
                            <div style={{ marginBottom: '12px', fontSize: '12px', color: '#666' }}>
                                <div style={{ fontWeight: 500, marginBottom: '2px' }}>Larmtider:</div>
                                {formatHoursSummary(c.alert_hours_start, c.alert_hours_end, c.alert_days_mask ?? ALL_DAYS)}
                            </div>
                            
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                                {testBtn(c, 'email')}
                                {testBtn(c, 'sms')}
                                {testBtn(c, 'push')}
                                {c.notify_push && (() => {
                                    const isThisDeviceSubscribed = currentEndpoint !== null;
                                    const loading = pushSubState[c.id] === 'loading';
                                    if (isThisDeviceSubscribed) {
                                        return (
                                            <button
                                                onClick={() => handleUnsubscribePush(c)}
                                                disabled={loading}
                                                style={btnStyle('#6c757d')}
                                            >
                                                {loading ? '…' : '🔕'}
                                            </button>
                                        );
                                    }
                                    return (
                                        <button
                                            onClick={() => handleSubscribePush(c)}
                                            disabled={loading}
                                            style={btnStyle('#17a2b8')}
                                        >
                                            {loading ? '…' : '🔔'}
                                        </button>
                                    );
                                })()}
                            </div>
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                                <button onClick={() => openAssignRules(c)} style={{ ...btnStyle('#17a2b8'), flex: 1 }}>Koppla</button>
                                <button onClick={() => onOpenEdit(c)} style={{ ...btnStyle('#ffc107', '#000'), flex: 1 }}>Ändra</button>
                                <button onClick={() => onDeleteContact(c.id)} style={{ ...btnStyle('#dc3545'), flex: 1 }}>Ta bort</button>
                            </div>
                        </div>
                    ))}
                </div>
            </>
        )}
    </div>
    );
};

export default ContactsTab;
